import { useMemo } from 'react';
import { extendTheme, ThemeConfig } from '@chakra-ui/react';
import type { ComponentStyleConfig } from '@chakra-ui/theme';

const config: ThemeConfig = {
	initialColorMode: 'light',
	useSystemColorMode: true,
};

const Container: ComponentStyleConfig = {
	baseStyle: {},
	variants: {
		default: {
			width: '100%',
			minHeight: '100vh',
			display: 'flex',
			background: 'neutral.150',
			maxWidth: 'initial',
			paddingTop: 'paddingTop.header',
		},
		content: {
			maxWidth: 'maxWidth.content',
			padding: '0',
			background: 'transparent',
		},
		highlightHeader: {
			minH: '30px',
			width: '100%',
			bg: 'primary.300',
			borderRadius: '100px',
			maxW: '100%',
			p: '3px 28px',
		},
		spinnerBox: {
			pos: 'absolute',
			top: '0',
			left: '0',
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center',
			height: '100vh',
			width: '100vw',
			background: 'neutral.150',
			maxW: 'unset',
			zIndex: 'loading',
			opacity: '70%',
		},
	},
	defaultProps: {
		variant: 'default',
	},
};

const Text: ComponentStyleConfig = {
	baseStyle: {
		fontFamily: 'Gotham, sans-serif',
		fontWeight: 'lighter',
		fontSize: { base: '1rem' },
	},
	variants: {
		default: {},
		required: {
			color: 'red.500',
		},
		error: {
			color: 'red.500',
			fontStyle: 'italic',
		},
		bold: {
			fontSize: '1.563rem',
			fontWeight: 600,
		},
		secondary: {
			color: 'secondary.500',
		},
		neutralZero: {
			color: 'secondary.50',
		},
		fourtiary: {
			color: 'fourtiary.100',
		},
	},
	defaultProps: {
		variant: 'default',
	},
};

const Link: ComponentStyleConfig = {
	baseStyle: {
		fontFamily: 'Gotham',
		fontWeight: 500,
		fontSize: { base: '1.25rem' },
	},
	variants: {
		default: {
			color: 'blue.800',
			textDecoration: 'underline',
		},
		secondary: {
			color: 'blue.500',
			textDecoration: 'underline',
		},
	},
	defaultProps: {
		variant: 'default',
	},
};

const Input: ComponentStyleConfig = {
	baseStyle: {},
	variants: {
		default: {
			field: {
				fontFamily: 'Gotham',
				fontWeight: 500,
				fontSize: { base: '1.25rem' },
				color: 'dark',
				border: '1px solid',
				boxShadow: '0 4px 4px var(--chakra-colors-neutral-300)',
				borderRadius: '5px',
				height: '3rem', //77px
				padding: '1.56rem 1.87rem',
				_placeholder: {
					color: 'neutral.100',
				},
				backgroundColor: 'neutral.0',
				borderColor: 'neutral.300',

				_invalid: {
					borderColor: 'red.500',
				},
				_disabled: {
					background: 'neutral.100',
					color: 'neutral.200',
					_placeholder: {
						color: 'neutral.200',
					},
				},
				_autofill: {
					background: 'neutral.90',
					backgroundColor: 'neutral.90 !important',
				},
			},
			_placeholder: {
				fontSize: { base: '1.25rem' },
			},
		},
	},
	defaultProps: {
		variant: 'default',
		size: '',
	},
};

const Select: ComponentStyleConfig = {
	baseStyle: {},
	variants: {
		default: {},
	},
	defaultProps: {
		variant: 'default',
		size: '',
	},
};

const FormLabel: ComponentStyleConfig = {
	baseStyle: {
		color: 'primary.500',
	},
	variants: {
		default: {},
	},
	defaultProps: {
		variant: 'default',
	},
};

const FormErrorMessage: ComponentStyleConfig = {
	baseStyle: {},
	variants: {
		default: {},
	},
	defaultProps: {
		variant: 'default',
	},
};

const Heading: ComponentStyleConfig = {
	baseStyle: {},
	variants: {
		default: {},
	},
	defaultProps: {
		variant: 'default',
		size: '',
	},
};

const Button: ComponentStyleConfig = {
	baseStyle: {
		height: '3rem',
		padding: '1rem',
		backgroundColor: 'primary.500',
		color: 'neutral.0',
	},
	variants: {
		default: {},
		ghost: {
			_hover: {
				background: 'transparent',
			},
		},
	},
	defaultProps: {
		variant: 'default',
		size: '',
	},
};

const Spinner: ComponentStyleConfig = {
	baseStyle: {},
	variants: {},
	defaultProps: {
		variant: 'default',
		size: 'lg',
	},
};

const Table: ComponentStyleConfig = {
	baseStyle: {},
	variants: {
		default: {},
	},
	defaultProps: {
		variant: 'default',
		size: '',
	},
};

export const useCustomTheme = (breakpoints: { [key: string]: string }) => {
	const customTheme = useMemo(
		() => ({
			styles: {
				global: {},
			},
			colors: {
				transparent: 'rgba(217, 217, 217, 0)',

				primary: {
					100: '#FBE4EB',
					200: '#F7BACE',
					225: 'rgba(247, 186, 206, 0.4)',
					250: '#F28EAE',
					275: 'rgba(247, 186, 206, 1)',
					290: 'rgba(251, 228, 235, 1)',
					300: '#EC618E',
					550: 'rgba(211, 28, 91, 0.9)',
					600: '#D31C5B',
				},
				neutral: {
					0: '#FFFFFF',
					80: '#F7F7F7',
					85: '#F2F2F2',
					90: '#EEEEEE',
					100: '#F0F0F0',
					110: '#ADB8C1',
					120: '#E6E7ED',
					150: '#EDE8F4',
					200: '#EDEDED',
					250: '#E2E2E2',
					300: '#D9D9D9',
					400: '#C4C4C4',
					500: '#9C9C9C',

					550: '#A3A3A3',
					570: '#E6E7ED80',
					600: '#566277',
					650: '#3F4275',
					800: '#484848',
					900: '#3b3a3a',
				},
				secondary: {
					50: '#C1C3D550',
					100: 'rgba(193, 195, 213, 0.50);',
					150: 'rgba(193, 195, 213, 1)',
					200: '#999DB8',
					220: '#585C87',
					300: '#73789B',
					600: '#506473',
					620: 'rgba(88, 92, 135, 1)',
					650: 'rgba(115, 120, 155, 0.50)',
					700: 'rgba(193, 195, 213, 0.50)',
					750: 'rgba(153, 157, 184, 1)',
					800: 'rgba(193, 195, 213, 0.20)',
					900: 'rgba(63, 66, 117, 1)',
				},
				terciary: {
					100: '#FEB511',
					200: 'rgba(140, 190, 41, 1)',
					300: '#8CBE29',
					500: '#21C3D9',
					600: '#0007B7',
					700: '#FE6611',
					800: '#2175D9',
					900: '#6b30af',
				},

				fourtiary: {
					100: '#3F4275',
				},
				dark: '#000000',
				gradients: {
					backgroundGradient: 'linear-gradient(287.98deg, #4A148C -14.25%, #D31C5B 105.51%)',
					header:
						'linear-gradient(45deg,  var(--chakra-colors-primary-300) 45%, var(--chakra-colors-terciary-600) 100%)',
					progressOrange: 'linear-gradient(287.98deg, #FEB511 24.25%, #FE6611 105.51%)',
					progressBlue: 'linear-gradient(287.98deg, #21C3D9 25%, #2175D9 105.51%)',
					progressGreen: 'linear-gradient(287.98deg, #F6FA3B -0.25%, #8CBE29 105.51%)',
				},
			},
			space: {
				paddingTop: {
					header: '80px',
				},
			},
			zIndices: {
				loading: '2000',
			},
			sizes: {
				maxWidth: {
					content: '1280px',
				},
			},
			fonts: {
				body: 'Gotham',
				heading: 'Gotham',
			},

			components: {
				Container,
				Heading,
				Text,
				FormLabel,
				FormError: FormErrorMessage,
				Button,
				Link,
				Input,
				Select,
				Spinner,
				Table,
			},
			size: {},
			fontSize: {},
		}),
		[],
	);

	return extendTheme({ breakpoints, ...customTheme, config });
};
